import {
  createContext,
  useContext,
  ReactNode,
  PropsWithChildren,
  forwardRef,
  useImperativeHandle,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import type {
  EmblaOptionsType,
  EmblaPluginType,
  EmblaCarouselType,
} from "embla-carousel";
import clsx from "clsx";

import { SimpleCarouselHandle } from "../../../types";

interface CarouselContext {
  viewportRef: (node: HTMLElement | null) => void;
  api: EmblaCarouselType | null;
}

const CarouselContext = createContext<CarouselContext | null>(null);

const useCarouselContext = () => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error("SimpleCarousel.Slide must be used within SimpleCarousel");
  }
  return context;
};

interface SimpleCarouselProps {
  children: ReactNode;
  options?: EmblaOptionsType;
  plugins?: EmblaPluginType[];
  className?: string;
}

const SimpleCarouselRoot = forwardRef<
  SimpleCarouselHandle,
  SimpleCarouselProps
>(
  (
    { children, options = { align: "start", loop: false }, plugins, className },
    ref,
  ) => {
    const [viewportRef, emblaApi] = useEmblaCarousel(options, plugins);

    useImperativeHandle(
      ref,
      () => ({
        scrollPrev: () => emblaApi?.scrollPrev(),
        scrollNext: () => emblaApi?.scrollNext(),
      }),
      [emblaApi],
    );

    return (
      <CarouselContext.Provider value={{ viewportRef, api: emblaApi ?? null }}>
        <div role="region" aria-roledescription="carousel">
          <div ref={viewportRef} className="overflow-hidden touch-pan-y">
            <div className={clsx("flex px-4 md:px-8", className)}>
              {children}
            </div>
          </div>
        </div>
      </CarouselContext.Provider>
    );
  },
);

interface SlideProps extends PropsWithChildren {
  className?: string;
}

const Slide = ({ children, className }: SlideProps) => {
  useCarouselContext();

  return (
    <div className={clsx("min-w-0 shrink-0 grow-0", className)}>{children}</div>
  );
};

export const SimpleCarousel = Object.assign(SimpleCarouselRoot, {
  Slide,
});
